import React from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';

import FallbackPage from './FallbackPage';
import { ASSET_NAME } from './LandingPage.duck';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

// FAQ structured data (schema.org/FAQPage) for the home page.
// Update questions and answers to reflect your most common customer enquiries.
const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Can I order flower delivery in Toronto online?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Yes — browse bouquets from local florists, pick what you like, and order directly through the platform. No phone calls needed.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I chat with the florist before ordering?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          "Absolutely. You can message sellers directly to ask about colours, sizing, customization, or delivery timing before you place the order. It's one of the more useful features on the platform, especially for anything custom or time-sensitive.",
      },
    },
    {
      '@type': 'Question',
      name: 'Can I schedule delivery for a specific date?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          "Most florists let you pick a delivery date at checkout, which is genuinely useful when you're ordering a few days early for a birthday or anniversary. Just don't leave it to the last minute and assume the date will sort itself out — confirm it before you complete the order.",
      },
    },
    {
      '@type': 'Question',
      name: 'Do florists send photos of the bouquet before delivery?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          "Many do. It's not guaranteed with every order, but a good number of florists on the platform will send a photo of the finished arrangement before it goes out — so you know exactly what's on its way.",
      },
    },
    {
      '@type': 'Question',
      name: 'Is same-day flower delivery available in Toronto?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'It depends on the florist and how early in the day you order. Same-day delivery is available from some sellers — just check the listing or ask via chat to confirm before you order.',
      },
    },
    {
      '@type': 'Question',
      name: 'Why order through LocalFlower instead of going directly to a florist?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'You get options. Instead of committing to one shop sight unseen, you can compare bouquets, read reviews, and make a more informed choice. For something as personal as flowers, that tends to make a real difference.',
      },
    },
  ],
};

export const LandingPageComponent = props => {
  const { pageAssetsData, inProgress, error } = props;

  return (
    <PageBuilder
      pageAssetsData={pageAssetsData?.[camelize(ASSET_NAME)]?.data}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
      schema={FAQ_SCHEMA}
    />
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  return { pageAssetsData, inProgress, error };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(connect(mapStateToProps))(LandingPageComponent);

export default LandingPage;
